<template>
  <div
    class="flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
  >
    <div class="relative shrink-0" :class="{ 'collab-glow': friend.user.openForCollabs }">
      <img
        :src="friend.user.avatar || '/images/default-avatar.svg'"
        :alt="friend.user.name"
        class="w-9 h-9 rounded-full object-cover"
        :class="friend.user.openForCollabs ? 'ring-2 ring-cyan-400/70 ring-offset-2 ring-offset-transparent' : ''"
      />
      <span
        v-if="status !== 'offline'"
        class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color,#0d0e12)]"
        :class="dotClass"
      ></span>
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-1.5 min-w-0">
        <p class="text-sm text-white font-semibold truncate">{{ friend.user.name }}</p>
        <span
          v-if="friend.user.openForCollabs"
          class="shrink-0 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"
          :title="$t('chat.badges.collab_tooltip')"
        >
          {{ $t('chat.badges.collab') }}
        </span>
      </div>
      <p class="text-[10px] text-text-muted capitalize">
        {{ status === 'offline' ? $t('chat.presence.offline') : $t(`chat.status.self.${status}`) }}
      </p>
    </div>

    <!-- Actions depend on status/direction -->
    <div class="flex items-center gap-1 shrink-0">
      <!-- Accepted friend: DM button + menu -->
      <template v-if="friend.status === 'accepted'">
        <button
          type="button"
          class="px-2 py-1 rounded text-[10px] bg-primary text-black font-semibold hover:bg-primary/90"
          :title="$t('chat.actions.open_dm')"
          @click="$emit('message', friend.user.id)"
        >
          <el-icon size="12"><ChatDotRound /></el-icon>
        </button>
        <el-dropdown trigger="click" placement="bottom-end">
          <button
            type="button"
            class="p-1 rounded text-text-muted hover:text-white hover:bg-white/5"
            :aria-label="'More actions for ' + friend.user.name"
          >
            <el-icon size="14"><MoreFilled /></el-icon>
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="$emit('remove', friend.id)">Unfriend</el-dropdown-item>
              <el-dropdown-item divided @click="$emit('block', friend.id)">
                <span class="text-red-400">Block</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>

      <!-- Incoming pending: accept + reject -->
      <template v-else-if="friend.status === 'pending' && friend.direction === 'incoming'">
        <button
          type="button"
          class="px-2 py-1 rounded text-[10px] bg-primary text-black font-semibold hover:bg-primary/90 disabled:opacity-50"
          :disabled="busy"
          @click="$emit('accept', friend.id)"
        >
          Accept
        </button>
        <button
          type="button"
          class="px-2 py-1 rounded text-[10px] bg-white/5 text-text-muted hover:text-white disabled:opacity-50"
          :disabled="busy"
          @click="$emit('reject', friend.id)"
        >
          Reject
        </button>
      </template>

      <!-- Outgoing pending: cancel -->
      <template v-else-if="friend.status === 'pending' && friend.direction === 'outgoing'">
        <span class="text-[10px] text-text-dim italic mr-1">Pending</span>
        <button
          type="button"
          class="px-2 py-1 rounded text-[10px] bg-white/5 text-text-muted hover:text-white disabled:opacity-50"
          :disabled="busy"
          @click="$emit('remove', friend.id)"
        >
          Cancel
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ChatDotRound, MoreFilled } from '@element-plus/icons-vue'
import { usePresence } from '~/composables/chat/usePresence'

const props = defineProps({
  friend: { type: Object, required: true },
  busy: { type: Boolean, default: false }
})
defineEmits(['message', 'accept', 'reject', 'remove', 'block'])

// The full presence state, not just online yes/no: an AFK or do-not-disturb
// friend showed a green dot and "Online" here while the online list next to
// it had them amber / red. Same colours and labels as that list and the
// status picker.
const { getStatus } = usePresence()
const status = computed(() => getStatus(props.friend.user.id))
const dotClass = computed(() =>
  status.value === 'afk' ? 'bg-amber-400' : status.value === 'dnd' ? 'bg-red-500' : 'bg-emerald-400'
)
</script>

<style scoped>
/* Soft cyan pulse around the avatar of users with the "Open for
   collaborations" flag on. Kept subtle so it reads as "hey, look at me"
   without drowning the presence list. Respects reduced-motion via the
   global @media override in assets/css/main.css. */
.collab-glow {
  animation: collabGlow 2.8s ease-in-out infinite;
  border-radius: 9999px;
}
@keyframes collabGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 242, 255, 0.28); }
  50%      { box-shadow: 0 0 0 6px rgba(0, 242, 255, 0.00); }
}
</style>
